import { useI18n } from '@react/hooks';
import { useState } from 'react';
import { downloadAttachment } from '@/features/attachment/download';
import { ActorReference } from '@/features/references/ActorReference';
import { formatDateTime } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';
import { Badge, Button, Alert } from '@react/components';
import type { ResourceCardData } from '@/features/resources/types';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

interface ResourceCardProps {
  data: ResourceCardData;
  onUnlink: (id: string) => void;
  onNavigate: (data: ResourceCardData) => void;
  borderColor?: string;
}

export function ResourceCard({ data, onUnlink, onNavigate, borderColor = 'border-transparent' }: ResourceCardProps) {
  const { t } = useI18n();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadAttachment(data.id, data.title);
    } catch (e) {
      setError(errMsg(e, t('task.attachmentsPanel.downloadError')));
    } finally { setDownloading(false); }
  };

  return (
    <>
      <div
        className={`group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent/50 cursor-pointer border-l-2 ${borderColor}`}
        onClick={() => onNavigate(data)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onNavigate(data); }
        }}
      >
        {data.icon ? (
          <svg className="h-4 w-4 flex-shrink-0" style={{ color: data.color || undefined }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d={data.icon} />
          </svg>
        ) : (
          <div className="h-4 w-4 flex-shrink-0 rounded-sm bg-muted" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-rail-foreground truncate">{data.title}</p>
          {data.subtitle && (
            <p className="text-xs text-muted-foreground truncate">{data.subtitle}</p>
          )}
          {(data.linkedById || data.linkedByName) && (
            <p className="text-xs text-muted-foreground truncate">
              {t('task.attachmentsPanel.addedBy')} <ActorReference id={data.linkedById} displayName={data.linkedByName} showAvatar={false} fallbackLabel={t('task.activityFeed.unknownUser')} />
            </p>
          )}
          {data.linkedAt && <time dateTime={data.linkedAt} className="block text-xs text-muted-foreground tabular-nums">{formatDateTime(data.linkedAt)}</time>}
        </div>
        {data.isAttachment && (
          <Button size="sm" variant="ghost" isLoading={downloading} aria-label={`${t('task.attachmentsPanel.download')}: ${data.title}`}
            onClick={(e) => { e.stopPropagation(); void download(); }}>
            {t('task.attachmentsPanel.download')}
          </Button>
        )}
        {data.status && (
          <Badge variant={data.statusVariant as any} size="sm" className="flex-shrink-0">
            {data.status}
          </Badge>
        )}
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          <button
            onClick={(e) => { e.stopPropagation(); onUnlink(data.id); }}
            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
            aria-label={t('rail.resources.unlink')}
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    {error && <Alert variant="error">{error}</Alert>}
    </>
  );
}
